<template>
  <div class="list-item-grid py-1">
    <div class="list-item-grid__icon-block">
      <v-icon :size="40" class="mr-4" :icon="`$${getIconName(ticket)}`"></v-icon>
    </div>

    <div class="list-item-grid__details-block d-flex flex-column justify-center px-2">
      <div :data-testid="`tickets-table-type-col-row-${index}`" class="subtitle2">
        {{ $t(`tickets.eventTriggers.${camelCase(ticket.eventTrigger)}`) }}
      </div>
      <div class="body2" :data-testid="`tickets-table-users-row-${index}`">
        <span v-if="shouldShowUsersNumber(ticket.eventType)">
          {{ $t("general.nUsers", { n: ticket.usersAffected }) }}
        </span>
        <span v-else>
          {{ displayedUsers[0] }}
          <span v-if="displayedUsers.length > 1">
            {{ $t("eventsPage.more", { n: displayedUsers.length - 1 }) }}
          </span>
        </span>
      </div>
      <div v-if="ticket.workspaceName" class="body2">
        {{ ticket.workspaceName }}
      </div>
      <div v-if="ticket.processName" class="body2">
        {{ ticket.processName }}
      </div>
      <div class="caption text-indigo-medium" :data-testid="`tickets-table-date-row-${index}`">
        {{ getFormattedDateTime(ticket.eventTime) }}
      </div>
      <slot name="bottom-extra" :ticket="ticket" />
    </div>

    <div class="list-item-grid__status-block">
      <ticket-list-status-icons
        class="mt-2 justify-end"
        :index="index"
        :potentially-protectable-users="getPotentiallyProtectableUsers(ticket)"
        :processed="ticket.processed"
      >
        <template v-if="ticket.workspaceId && ticket.hasUnreadComment" #soc-comments-label>
          <v-icon size="25" icon="$comments"></v-icon>
        </template>
      </ticket-list-status-icons>
    </div>

    <div class="list-item-grid__bottom-block">
      <div class="d-flex align-center mb-2">
        <users-say
          :suspicious-vote-count="ticket.suspiciousVoteCount"
          :safe-vote-count="ticket.safeVoteCount"
        >
          <template #soc-status-label>
            <div
              v-if="showProcessStatus(ticket)"
              class="caption mt-1"
              :class="{
                'text-green-dark': ticket.processStatus === EdrProcessStatus.ALLOWED,
                'text-red-dark': ticket.processStatus === EdrProcessStatus.BLOCKED,
              }"
            >
              {{ $t(`general.${ticket.processStatus}`) }}
            </div>
            <slot name="soc-status-extra" :ticket="ticket" />
          </template>
        </users-say>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends Ticket">
import camelCase from "lodash/camelCase";
import { computed } from "vue";
import { type Ticket } from "@/_store/tickets/tickets.module";
import { EdrProcessStatus } from "@/constants/edr";
import { getFormattedDateTime } from "@/_helpers/date";
import {
  getIconName,
  getPotentiallyProtectableUsers,
  getTicketDisplayedUsers,
  shouldShowUsersNumber,
  showProcessStatus,
} from "@/_helpers/ticket-helpers";
import UsersSay from "@/components/tickets/UsersSay.vue";
import TicketListStatusIcons from "@/components/TicketListStatusIcons.vue";

const props = defineProps<{
  ticket: T;
  index: number;
}>();

const displayedUsers = computed(() => getTicketDisplayedUsers(props.ticket));
</script>
